
import { getETAFile, getETAList, lookupCustomer, sendETAReport } from '@/api';
import LookupCustomerCode from '@/components/jobBooking/AdvancedJobBooking/LookupCustomerCode.vue';
import DateSelect from '@/components/dashBoard/selectsFields/components/DateSelect/DateSelect.vue';
import BackButton from '@/components/UI/backButton/BackButton.vue';
import Button from '@/components/UI/button/Button.vue';
import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import { Header, User } from '@/models/main';
import { ETAReportResponse } from '@/models/reports';
import { notificationErrorText } from '@/utils/PortalConstants';
import { mapState } from 'vuex';
import * as routeNames from '@/router/routeNames';

@Component({
  components: {
    Button,
    BackButton,
    DateSelect,
    LookupCustomerCode,
  },
  computed: {
    ...mapState('moduleUser', { userSelectedDates: 'userSelectedDates', user: 'user' }),
  },
})
export default class ETAReport extends Vue {
  public routeNames = routeNames;
  private isLoading = false;
  private isSendReportLoading = false;
  private isDownloading = false;
  private dates: string[] = [];
  private etaReport: ETAReportResponse[] = [];
  private userSelectedDates!: string[];
  private user!: User;

  // Table related
  private expanded: ETAReportResponse[] = [];
  private headers: Header[] = [
    { text: 'Id', value: 'job_id', align: 'left' },
    { text: 'Cost Service', value: 'cost_service', align: 'left', sortable: false },
    { text: 'Reference 1', value: 'ref1', align: 'left', sortable: false },
    { text: 'Stop Name', value: 'name', align: 'left' },
    { text: 'Stop Suburb', value: 'suburb', align: 'left', sortable: false },
    { text: 'Stop State', value: 'state', align: 'left', sortable: false },
    { text: 'ETA', value: 'eta', align: 'left' },
    { text: 'Link', value: 'link', align: 'left', sortable: false },
  ];
  private expandRow(job: ETAReportResponse): void {
    if (this.expanded[0] === job) {
      this.expanded = [];
    } else {
      this.expanded = [];
      this.expanded.push(job);
    }
  }
  //Customer code lookup related
  private customerCode = '';
  private autocompleteCustomer = lookupCustomer;
  private setCustomerCode(customerCode): void {
    if (customerCode) {
      this.customerCode = customerCode;
    }
  }
  private clearCustomCode(): void {
    this.customerCode = '';
  }
  // Date lookup
  private changeDate(date: string): void {
    this.dates[0] = date;
    if (this.customerCode) {
      this.getETAReport();
    }
  }
  // ETA report
  private getETAReport() {
    this.isLoading = true;
    getETAList(this.dates, this.customerCode)
      .then(res => {
        this.etaReport = res.data.results;
      })
      .catch(e => {
        if (e.response.status === 403) {
          this.$notify({
            group: 'network',
            title: 'Error getting ETA report',
            text: 'No access rights to get ETA report',
            type: 'warn',
          });
        } else {
          this.$notify({
            group: 'network',
            title: 'Error getting ETA report',
            text: notificationErrorText,
            type: 'warn',
          });
        }
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  private downloadETAReport(downloadType = 'JSON') {
    if (this.customerCode) {
      this.isDownloading = true;
      getETAFile(this.dates, this.customerCode, downloadType)
        .then(res => {
          this.blobDownloader(res, downloadType);
        })
        .catch(() => {
          this.$notify({
            group: 'network',
            title: 'Error getting ETA report',
            text: notificationErrorText,
            type: 'warn',
          });
        })
        .finally(() => {
          this.isDownloading = false;
        });
    }
  }
  private blobDownloader(response, type) {
    let blob;
    let fileName;
    if (type === 'PDF') {
      blob = new Blob([response.data], { type: 'application/pdf' });
      fileName = this.customerCode + '_' + this.dates[0] + '_report_eta.pdf';
    } else if (type === 'CSV') {
      blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
      fileName = this.customerCode + '_' + this.dates[0] + '_report_eta.csv';
    }
    const link = document.createElement('a');
    link.setAttribute('href', URL.createObjectURL(blob));
    link.setAttribute('download', fileName);
    link.click();
    URL.revokeObjectURL(link.href);
    link.remove();
  }

  private sendReport(): void {
    if (this.customerCode) {
      this.isSendReportLoading = true;
      sendETAReport(this.dates, this.customerCode)
        .then(res => {
          if (res.status === 200) {
            this.$notify({
              group: 'network',
              text: 'The report was successfully sent',
              type: 'success',
              duration: 3000,
            });
          } else {
            this.$notify({
              group: 'network',
              title: 'Error sending the report',
              text: notificationErrorText,
              type: 'warn',
            });
          }
        })
        .catch(() => {
          this.$notify({
            group: 'network',
            title: 'Error sending the report',
            text: notificationErrorText,
            type: 'warn',
          });
        })
        .finally(() => {
          this.isSendReportLoading = false;
        });
    }
  }

  private mounted(): void {
    this.dates[0] = this.userSelectedDates[0];
    if (this.user && this.user.customerCode) {
      this.customerCode = this.user.customerCode;
    }
  }
}
